/* ==========================================================================
   Stats Tab Controller
   Tracks overall average, high game, high series, ball performance stats,
   night-by-night history, and saved notes log.
   ========================================================================== */

import { appState } from './state.js';

export function initStats() {
  const statOverallAvg = document.getElementById('statOverallAvg');
  const statHighGame = document.getElementById('statHighGame');
  const statHighSeries = document.getElementById('statHighSeries');
  const statTotalGames = document.getElementById('statTotalGames');

  const ballStatsContainer = document.getElementById('ballStatsContainer');
  const historyListContainer = document.getElementById('historyListContainer');
  const statsNotesList = document.getElementById('statsNotesList');

  const btnLogPastNight = document.getElementById('btnLogPastNight');
  const pastNightModal = document.getElementById('pastNightModal');
  const btnClosePastNight = document.getElementById('btnClosePastNight');
  const btnCancelPastNight = document.getElementById('btnCancelPastNight');
  const pastNightForm = document.getElementById('pastNightForm');
  const pastBall1Select = document.getElementById('pastBall1Select');
  const pastBall2Select = document.getElementById('pastBall2Select');
  const pastBall3Select = document.getElementById('pastBall3Select');

  let currentSeasonFilter = 'all';

  function populatePastNightBallDropdowns() {
    const s = appState.get();
    const dropdowns = [pastBall1Select, pastBall2Select, pastBall3Select];
    dropdowns.forEach(select => {
      if (!select) return;
      select.innerHTML = '<option value="">-- Select Ball --</option>';
      s.arsenal.forEach(ball => {
        const opt = document.createElement('option');
        opt.value = ball.id;
        opt.textContent = `${ball.brand} ${ball.name}`;
        select.appendChild(opt);
      });
    });
  }

  function updateStatsView() {
    const s = appState.get();
    const history = s.history || [];
    const arsenal = s.arsenal || [];

    // Calculate Overall Stats
    let totalScore = 0;
    let totalGames = 0;
    let highGame = 0;
    let highSeries = 0;

    // Track per-ball stats
    const ballStats = {};
    arsenal.forEach(b => {
      ballStats[b.id] = { ball: b, games: 0, totalScore: 0, highGame: 0 };
    });

    history.forEach(night => {
      const g1 = typeof night.game1 === 'number' ? night.game1 : null;
      const g2 = typeof night.game2 === 'number' ? night.game2 : null;
      const g3 = typeof night.game3 === 'number' ? night.game3 : null;

      const nightGames = [
        { score: g1, ballId: night.ballGame1 },
        { score: g2, ballId: night.ballGame2 },
        { score: g3, ballId: night.ballGame3 }
      ];

      let nightSeriesTotal = 0;
      let nightGamesCount = 0;

      nightGames.forEach(g => {
        if (g.score !== null && !isNaN(g.score)) {
          totalScore += g.score;
          totalGames++;
          nightSeriesTotal += g.score;
          nightGamesCount++;

          if (g.score > highGame) highGame = g.score;

          // Ball breakdown
          if (g.ballId && ballStats[g.ballId]) {
            ballStats[g.ballId].games++;
            ballStats[g.ballId].totalScore += g.score;
            if (g.score > ballStats[g.ballId].highGame) {
              ballStats[g.ballId].highGame = g.score;
            }
          }
        }
      });

      if (nightGamesCount === 3 && nightSeriesTotal > highSeries) {
        highSeries = nightSeriesTotal;
      }
    });

    const currentUser = (s.currentUser || '').toLowerCase();
    const leagueBowlers = s.leagueBowlers || [];

    let matchedBowler = null;
    if (currentUser) {
      matchedBowler = leagueBowlers.find(b => (b.BowlerName || '').toLowerCase().includes(currentUser));
    }
    if (!matchedBowler) {
      matchedBowler = leagueBowlers.find(b => {
        const name = (b.BowlerName || '').toLowerCase();
        return name.includes('eric') || name.includes('matson');
      });
    }

    const isEric = !currentUser || currentUser.includes('eric');
    const fallbackAvg = matchedBowler && matchedBowler.Average ? matchedBowler.Average : (isEric ? '182.2' : '--');
    const fallbackHighGame = matchedBowler && matchedBowler.HighScratchGame ? matchedBowler.HighScratchGame : (isEric ? 224 : '--');
    const fallbackHighSeries = matchedBowler && matchedBowler.HighScratchSeries ? matchedBowler.HighScratchSeries : (isEric ? 595 : '--');
    const fallbackTotalGames = matchedBowler && matchedBowler.TotalGames ? matchedBowler.TotalGames : (isEric ? 9 : 0);

    const overallAvg = totalGames > 0 
      ? (totalScore / totalGames).toFixed(1) 
      : fallbackAvg;

    const finalHighGame = highGame > 0 ? highGame : fallbackHighGame;
    const finalHighSeries = highSeries > 0 ? highSeries : fallbackHighSeries;
    const finalTotalGames = totalGames > 0 ? totalGames : fallbackTotalGames;

    if (statOverallAvg) statOverallAvg.textContent = overallAvg;
    if (statHighGame) statHighGame.textContent = finalHighGame;
    if (statHighSeries) statHighSeries.textContent = finalHighSeries;
    if (statTotalGames) statTotalGames.textContent = finalTotalGames;

    // Render Ball Usage & Stats Grid
    if (ballStatsContainer) {
      ballStatsContainer.innerHTML = '';
      arsenal.forEach(b => {
        const bs = ballStats[b.id] || { games: 0, totalScore: 0, highGame: 0 };
        const bAvg = bs.games > 0 ? (bs.totalScore / bs.games).toFixed(1) : '--';
        const bHigh = bs.highGame > 0 ? bs.highGame : '--';

        const color0 = b.colors && b.colors[0] ? b.colors[0] : '#3b82f6';
        const color1 = b.colors && b.colors[1] ? b.colors[1] : '#1e3a8a';
        const color2 = b.colors && b.colors[2] ? b.colors[2] : '#000000';

        const card = document.createElement('div');
        card.className = 'ball-stat-card';
        card.innerHTML = `
          <div class="ball-stat-top">
            <div class="ball-sphere-sm" style="background: radial-gradient(circle at 10px 10px, ${color0} 0%, ${color1} 60%, ${color2} 100%);"></div>
            <div>
              <div class="ball-stat-name">${b.brand} ${b.name}</div>
              <div class="ball-stat-sub">${b.coverType}</div>
            </div>
          </div>
          <div class="ball-stat-metrics">
            <div class="b-metric">
              <span class="bm-val">${bs.games}</span>
              <span class="bm-lbl">Games</span>
            </div>
            <div class="b-metric">
              <span class="bm-val">${bAvg}</span>
              <span class="bm-lbl">Avg Score</span>
            </div>
            <div class="b-metric">
              <span class="bm-val">${bHigh}</span>
              <span class="bm-lbl">High Game</span>
            </div>
          </div>
        `;
        ballStatsContainer.appendChild(card);
      });
    }

    // Render Night-by-Night History Cards
    // Render Night-by-Night History Cards
    if (historyListContainer) {
      historyListContainer.innerHTML = '';

      // Update Season Filter counts
      const countAll = history.length;
      const count2627 = history.filter(h => h.season === '2026-2027').length;
      const count2526 = history.filter(h => h.season === '2025-2026').length;
      const count2425 = history.filter(h => h.season === '2024-2025').length;
      const count2324 = history.filter(h => h.season === '2023-2024').length;

      const cAllEl = document.getElementById('countAllSeasons');
      const c2627El = document.getElementById('count2627');
      const c2526El = document.getElementById('count2526');
      const c2425El = document.getElementById('count2425');
      const c2324El = document.getElementById('count2324');
      const hBadgeEl = document.getElementById('historyCountBadge');

      if (cAllEl) cAllEl.textContent = countAll;
      if (c2627El) c2627El.textContent = count2627;
      if (c2526El) c2526El.textContent = count2526;
      if (c2425El) c2425El.textContent = count2425;
      if (c2324El) c2324El.textContent = count2324;
      if (hBadgeEl) hBadgeEl.textContent = `${countAll} Nights`;

      // Filter by active season
      let filteredHistory = [...history];
      if (currentSeasonFilter !== 'all') {
        filteredHistory = filteredHistory.filter(h => h.season === currentSeasonFilter);
      }

      if (filteredHistory.length === 0) {
        historyListContainer.innerHTML = '<p class="subtext text-center" style="padding: 24px 0;">No league nights found for this season filter.</p>';
      } else {
        // Sort descending by date / week
        const sortedHistory = [...filteredHistory].sort((a, b) => {
          if (a.date && b.date) return new Date(b.date) - new Date(a.date);
          return (b.week || 0) - (a.week || 0);
        });

        sortedHistory.forEach(item => {
          const card = document.createElement('div');
          card.className = 'card history-card';

          const getBallBadge = (ballId, ballRaw) => {
            if (!ballId && !ballRaw) return '';
            const b = arsenal.find(x => 
              (ballId && x.id === ballId) || 
              (ballId && x.name.toLowerCase() === ballId.toLowerCase()) ||
              (ballRaw && (x.name.toLowerCase().includes(ballRaw.toLowerCase()) || ballRaw.toLowerCase().includes(x.name.toLowerCase())))
            );
            const displayName = b ? b.name : (ballRaw || ballId);
            const c = b && b.colors && b.colors[0] ? b.colors[0] : '#0d9488';
            return `<span class="ball-pill-tag" style="border-left: 3px solid ${c};">${displayName}</span>`;
          };

          const isTopSeries = highSeries > 0 && item.seriesTotal === highSeries;

          card.innerHTML = `
            <div class="history-card-header">
              <div>
                <div class="history-meta-badges">
                  ${item.season || item.weekLabel ? `<span class="badge-week">${item.season || ''} • ${item.weekLabel || ('Week ' + (item.week || ''))}</span>` : ''}
                  ${item.handicap !== undefined && item.handicap !== null ? `<span class="badge-hdcp">HDCP: ${item.handicap}</span>` : ''}
                  ${item.result ? `<span class="badge-result">${item.result}</span>` : ''}
                  ${isTopSeries ? `<span class="badge-tag" style="background: rgba(234, 179, 8, 0.2); color: #eab308; font-size: 0.68rem; font-weight: 700; border-radius: 4px; padding: 2px 6px;">⭐ Career High Series</span>` : ''}
                  <button type="button" class="badge-edit-btn" data-id="${item.id}" title="Edit this week's scores"><i data-lucide="edit-3"></i> Edit</button>
                </div>
                <h3 class="history-opp">vs. ${item.opponent || 'League Opponent'}</h3>
                <span class="history-date"><i data-lucide="calendar"></i> ${item.date || 'League Night'}</span>
              </div>
              <div class="history-series-badge ${isTopSeries ? 'highlight-gold' : ''}">
                <span class="hs-val">${item.seriesTotal || '--'}</span>
                <span class="hs-lbl">Scratch (${item.seriesAvg || '--'} Avg)${item.hdcpTotal ? `<br><span class="chip-hdcp-sub">${item.hdcpTotal} Hdcp</span>` : ''}</span>
              </div>
            </div>

            <div class="history-games-row">
              <div class="history-game-chip">
                <span class="g-lbl">Game 1</span>
                <span class="g-val">${item.game1 !== null ? item.game1 : '--'}${item.game1Hdcp ? ` <small class="chip-hdcp-sub">(${item.game1Hdcp})</small>` : ''}</span>
                ${getBallBadge(item.ballGame1, item.ballRaw1)}
              </div>
              <div class="history-game-chip">
                <span class="g-lbl">Game 2</span>
                <span class="g-val">${item.game2 !== null ? item.game2 : '--'}${item.game2Hdcp ? ` <small class="chip-hdcp-sub">(${item.game2Hdcp})</small>` : ''}</span>
                ${getBallBadge(item.ballGame2, item.ballRaw2)}
              </div>
              <div class="history-game-chip">
                <span class="g-lbl">Game 3</span>
                <span class="g-val">${item.game3 !== null ? item.game3 : '--'}${item.game3Hdcp ? ` <small class="chip-hdcp-sub">(${item.game3Hdcp})</small>` : ''}</span>
                ${getBallBadge(item.ballGame3, item.ballRaw3)}
              </div>
            </div>

            <div class="history-card-footer">
              <span class="history-pts"><i data-lucide="award"></i> Result: <strong>${item.result || item.pointsWon || 'N/A'}</strong></span>
              ${item.notes ? `<p class="history-note" title="${item.notes}"><i data-lucide="message-square"></i> ${item.notes}</p>` : ''}
              <div class="history-card-actions">
                <button type="button" class="btn-edit-history" data-id="${item.id}" title="Edit Match Scores"><i data-lucide="edit-3"></i></button>
                <button type="button" class="btn-delete-history" data-id="${item.id}" title="Delete Record"><i data-lucide="trash-2"></i></button>
              </div>
            </div>
          `;

          historyListContainer.appendChild(card);
        });

        // Add Edit event listeners
        document.querySelectorAll('.badge-edit-btn, .btn-edit-history').forEach(btn => {
          btn.addEventListener('click', (e) => {
            const hId = e.currentTarget.getAttribute('data-id');
            openEditModal(hId);
          });
        });

        // Add Delete event listeners
        document.querySelectorAll('.btn-delete-history').forEach(btn => {
          btn.addEventListener('click', (e) => {
            const hId = e.currentTarget.getAttribute('data-id');
            if (confirm('Are you sure you want to delete this league night record?')) {
              const curState = appState.get();
              const filtered = (curState.history || []).filter(h => h.id !== hId);
              appState.set({ history: filtered });
              updateStatsView();
            }
          });
        });
      }
    }

    // Render Notes Log
    if (statsNotesList) {
      statsNotesList.innerHTML = '';
      const notes = s.notes || [];
      if (notes.length === 0) {
        statsNotesList.innerHTML = '<li class="note-item"><p class="note-text">No notes saved.</p></li>';
      } else {
        notes.forEach(n => {
          const li = document.createElement('li');
          li.className = 'note-item';
          li.innerHTML = `
            <span class="note-time">${n.context}</span>
            <p class="note-text">${n.text}</p>
          `;
          statsNotesList.appendChild(li);
        });
      }
    }

    if (window.lucide) {
      window.lucide.createIcons();
    }
  }

  // Modal Handlers for Log Past Night
  const openPastModal = () => {
    populatePastNightBallDropdowns();
    if (pastNightModal) pastNightModal.classList.remove('hidden');
  };
  const closePastModal = () => {
    if (pastNightModal) pastNightModal.classList.add('hidden');
  };

  if (btnLogPastNight) btnLogPastNight.addEventListener('click', openPastModal);
  if (btnClosePastNight) btnClosePastNight.addEventListener('click', closePastModal);
  if (btnCancelPastNight) btnCancelPastNight.addEventListener('click', closePastModal);

  if (pastNightForm) {
    pastNightForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const dateVal = document.getElementById('pastDateInput').value || new Date().toISOString().split('T')[0];
      const oppVal = document.getElementById('pastOpponentInput').value.trim() || 'League Opponent';
      const g1 = parseInt(document.getElementById('pastGame1Input').value) || null;
      const g2 = parseInt(document.getElementById('pastGame2Input').value) || null;
      const g3 = parseInt(document.getElementById('pastGame3Input').value) || null;
      const b1 = pastBall1Select?.value || '';
      const b2 = pastBall2Select?.value || '';
      const b3 = pastBall3Select?.value || '';
      const pts = document.getElementById('pastPointsInput').value.trim() || '5.0 / 7.0';
      const notes = document.getElementById('pastNotesInput').value.trim() || '';

      let total = 0;
      let count = 0;
      if (g1 !== null) { total += g1; count++; }
      if (g2 !== null) { total += g2; count++; }
      if (g3 !== null) { total += g3; count++; }
      const avg = count > 0 ? parseFloat((total / count).toFixed(1)) : 0;

      const newRecord = {
        id: 'h_' + Date.now(),
        date: dateVal,
        opponent: oppVal,
        game1: g1,
        game2: g2,
        game3: g3,
        ballGame1: b1,
        ballGame2: b2,
        ballGame3: b3,
        seriesTotal: total,
        seriesAvg: avg,
        pointsWon: pts,
        notes: notes
      };

      const s = appState.get();
      const updatedHistory = s.history ? [newRecord, ...s.history] : [newRecord];

      // Update gamesBowled count for used balls
      const usageCounts = {};
      if (g1 !== null && b1) usageCounts[b1] = (usageCounts[b1] || 0) + 1;
      if (g2 !== null && b2) usageCounts[b2] = (usageCounts[b2] || 0) + 1;
      if (g3 !== null && b3) usageCounts[b3] = (usageCounts[b3] || 0) + 1;

      const updatedArsenal = s.arsenal.map(ball => {
        if (usageCounts[ball.id]) {
          return { ...ball, gamesBowled: (ball.gamesBowled || 0) + usageCounts[ball.id] };
        }
        return ball;
      });

      appState.set({ history: updatedHistory, arsenal: updatedArsenal });
      closePastModal();
      updateStatsView();
      alert('Past league night added successfully!');
    });
  }

  // --- Edit Match Modal Logic ---
  const editHistoryModal = document.getElementById('editHistoryModal');
  const btnCloseEditHistory = document.getElementById('btnCloseEditHistory');
  const btnCancelEditHistory = document.getElementById('btnCancelEditHistory');
  const editHistoryForm = document.getElementById('editHistoryForm');
  const editHistoryId = document.getElementById('editHistoryId');
  const editSeasonInput = document.getElementById('editSeasonInput');
  const editWeekInput = document.getElementById('editWeekInput');
  const editDateInput = document.getElementById('editDateInput');
  const editHandicapInput = document.getElementById('editHandicapInput');
  const editOpponentInput = document.getElementById('editOpponentInput');
  const editGame1Input = document.getElementById('editGame1Input');
  const editGame2Input = document.getElementById('editGame2Input');
  const editGame3Input = document.getElementById('editGame3Input');
  const editBall1Select = document.getElementById('editBall1Select');
  const editBall2Select = document.getElementById('editBall2Select');
  const editBall3Select = document.getElementById('editBall3Select');
  const editDisplayScratch = document.getElementById('editDisplayScratch');
  const editDisplayHdcp = document.getElementById('editDisplayHdcp');
  const editDisplayAvg = document.getElementById('editDisplayAvg');
  const editPointsInput = document.getElementById('editPointsInput');
  const editNotesInput = document.getElementById('editNotesInput');

  function populateEditBallDropdowns(b1Val, b2Val, b3Val) {
    const s = appState.get();
    const dropdowns = [
      { el: editBall1Select, val: b1Val },
      { el: editBall2Select, val: b2Val },
      { el: editBall3Select, val: b3Val }
    ];

    dropdowns.forEach(({ el, val }) => {
      if (!el) return;
      el.innerHTML = '<option value="">-- No Ball Specified --</option>';
      (s.arsenal || []).forEach(ball => {
        const opt = document.createElement('option');
        opt.value = ball.id;
        opt.textContent = `${ball.brand} ${ball.name}`;
        if (ball.id === val || ball.name.toLowerCase() === (val || '').toLowerCase()) {
          opt.selected = true;
        }
        el.appendChild(opt);
      });
    });
  }

  function calculateEditTotals() {
    const hdcp = parseInt(editHandicapInput?.value) || 0;
    const g1 = editGame1Input?.value !== '' ? parseInt(editGame1Input.value) : null;
    const g2 = editGame2Input?.value !== '' ? parseInt(editGame2Input.value) : null;
    const g3 = editGame3Input?.value !== '' ? parseInt(editGame3Input.value) : null;

    let total = 0;
    let count = 0;
    if (g1 !== null) { total += g1; count++; }
    if (g2 !== null) { total += g2; count++; }
    if (g3 !== null) { total += g3; count++; }

    const hdcpTotal = count > 0 ? (total + (count * hdcp)) : 0;
    const avg = count > 0 ? (total / count).toFixed(1) : '0.0';

    if (editDisplayScratch) editDisplayScratch.textContent = count > 0 ? total : '--';
    if (editDisplayHdcp) editDisplayHdcp.textContent = count > 0 ? hdcpTotal : '--';
    if (editDisplayAvg) editDisplayAvg.textContent = count > 0 ? avg : '--';
  }

  [editGame1Input, editGame2Input, editGame3Input, editHandicapInput].forEach(inp => {
    if (inp) inp.addEventListener('input', calculateEditTotals);
  });

  function openEditModal(hId) {
    const s = appState.get();
    const record = (s.history || []).find(h => h.id === hId);
    if (!record) return;

    if (editHistoryId) editHistoryId.value = record.id;
    if (editSeasonInput) editSeasonInput.value = record.season || '2026-2027';
    if (editWeekInput) editWeekInput.value = record.week || 1;
    if (editDateInput) editDateInput.value = record.date || '';
    if (editHandicapInput) editHandicapInput.value = record.handicap !== undefined && record.handicap !== null ? record.handicap : '';
    if (editOpponentInput) editOpponentInput.value = record.opponent || '';
    if (editGame1Input) editGame1Input.value = record.game1 !== null && record.game1 !== undefined ? record.game1 : '';
    if (editGame2Input) editGame2Input.value = record.game2 !== null && record.game2 !== undefined ? record.game2 : '';
    if (editGame3Input) editGame3Input.value = record.game3 !== null && record.game3 !== undefined ? record.game3 : '';
    if (editPointsInput) editPointsInput.value = record.result || record.pointsWon || '';
    if (editNotesInput) editNotesInput.value = record.notes || '';

    populateEditBallDropdowns(record.ballGame1, record.ballGame2, record.ballGame3);
    calculateEditTotals();

    if (editHistoryModal) editHistoryModal.classList.remove('hidden');
    if (window.lucide) window.lucide.createIcons();
  }

  const closeEditModal = () => {
    if (editHistoryModal) editHistoryModal.classList.add('hidden');
  };

  if (btnCloseEditHistory) btnCloseEditHistory.addEventListener('click', closeEditModal);
  if (btnCancelEditHistory) btnCancelEditHistory.addEventListener('click', closeEditModal);

  if (editHistoryForm) {
    editHistoryForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const hId = editHistoryId.value;
      const s = appState.get();
      const idx = (s.history || []).findIndex(h => h.id === hId);
      if (idx === -1) return;

      const seasonVal = editSeasonInput.value.trim() || '2026-2027';
      const weekVal = parseInt(editWeekInput.value) || 1;
      const dateVal = editDateInput.value || s.history[idx].date;
      const hdcpVal = editHandicapInput.value !== '' ? parseInt(editHandicapInput.value) : null;
      const oppVal = editOpponentInput.value.trim() || 'League Opponent';

      const g1 = editGame1Input.value !== '' ? parseInt(editGame1Input.value) : null;
      const g2 = editGame2Input.value !== '' ? parseInt(editGame2Input.value) : null;
      const g3 = editGame3Input.value !== '' ? parseInt(editGame3Input.value) : null;

      const b1 = editBall1Select.value;
      const b2 = editBall2Select.value;
      const b3 = editBall3Select.value;

      const pts = editPointsInput.value.trim();
      const notes = editNotesInput.value.trim();

      let scratch = 0;
      let count = 0;
      if (g1 !== null) { scratch += g1; count++; }
      if (g2 !== null) { scratch += g2; count++; }
      if (g3 !== null) { scratch += g3; count++; }

      const avg = count > 0 ? parseFloat((scratch / count).toFixed(1)) : 0;
      const hdcpTotal = count > 0 && hdcpVal !== null ? (scratch + (count * hdcpVal)) : null;

      const isChamp = weekVal === 28 && seasonVal === '2023-2024';
      const weekLbl = isChamp ? `Week ${weekVal} (Championship)` : `Week ${weekVal}`;

      const updatedRecord = {
        ...s.history[idx],
        season: seasonVal,
        week: weekVal,
        weekLabel: weekLbl,
        date: dateVal,
        opponent: oppVal,
        handicap: hdcpVal,
        game1: g1,
        game2: g2,
        game3: g3,
        game1Hdcp: g1 !== null && hdcpVal !== null ? g1 + hdcpVal : null,
        game2Hdcp: g2 !== null && hdcpVal !== null ? g2 + hdcpVal : null,
        game3Hdcp: g3 !== null && hdcpVal !== null ? g3 + hdcpVal : null,
        ballGame1: b1,
        ballGame2: b2,
        ballGame3: b3,
        seriesTotal: scratch,
        hdcpTotal: hdcpTotal,
        seriesAvg: avg,
        pointsWon: pts || 'League Night',
        result: pts || '',
        notes: notes
      };

      const updatedHistory = [...s.history];
      updatedHistory[idx] = updatedRecord;

      appState.set({ history: updatedHistory });
      closeEditModal();
      updateStatsView();
      alert(`Updated ${seasonVal} Week ${weekVal} successfully!`);
    });
  }

  // Season Filter buttons
  const seasonPills = document.querySelectorAll('.season-pill');
  seasonPills.forEach(pill => {
    pill.addEventListener('click', (e) => {
      seasonPills.forEach(p => p.classList.remove('active'));
      e.currentTarget.classList.add('active');
      currentSeasonFilter = e.currentTarget.getAttribute('data-season') || 'all';
      updateStatsView();
    });
  });

  updateStatsView();
  appState.subscribe(updateStatsView);
}
